import React, { useState, useMemo, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useForm, useFieldArray, Controller, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { pdf } from "@react-pdf/renderer";
import {
  Container,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Divider,
  Grid,
  Alert,
  Snackbar,
} from "@mui/material";
import { ArrowBack, Download } from "@mui/icons-material";
// import CreditNotePDF from './CreditNotePDF';

// Catálogo 09 de SUNAT - Motivos de Nota de Crédito
const sunatReasons = [
  { code: "01", label: "01 - Anulación de la operación" },
  { code: "02", label: "02 - Anulación por error en el RUC (Solo Factura)" },
  { code: "03", label: "03 - Corrección por error en la descripción" },
  { code: "06", label: "06 - Devolución por garantía" },
  { code: "07", label: "07 - Devolución de bienes" },
  { code: "08", label: "08 - Descuento global" },
  { code: "09", label: "09 - Descuento por ítem" },
];

const TAX_RATE = 0.16;

const schema = yup.object().shape({
  reasonCode: yup.string().required("Debe seleccionar un motivo SUNAT"),
  items: yup
    .array()
    .of(
      yup.object().shape({
        description: yup.string().required(),
        quantity: yup
          .number()
          .min(0, "No puede ser negativo")
          .required()
          .typeError("Debe ser un número"),
        amount: yup
          .number()
          .min(0, "No puede ser negativo")
          .required()
          .typeError("Debe ser un número"),
      })
    )
    .min(1),
});

export default function CreateNT({ documents, addDocument }) {
  //   const { parentId } = useParams(); // ID de la Factura/Boleta original
  const location = useLocation();
  const history = useHistory();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Buscar el documento original
  //   const parentDoc = documents.find(doc => doc.id === parentId);
  const parentDoc = location.state.documents || [];

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      reasonCode: "",
      items: parentDoc
        ? parentDoc.paymentDetails?.map((item) => ({ ...item }))
        : [],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "items" });
  const watchItems = useWatch({ control, name: "items" });

  // Cálculos
  const totals = useMemo(() => {
    if (!watchItems) return { subtotal: 0, tax: 0, total: 0 };
    const subtotal = watchItems.reduce(
      (sum, item) =>
        sum + (Number(item?.quantity) || 0) * (Number(item?.amount) || 0),
      0
    );
    const tax = parentDoc?.docType === "FACTURA" ? subtotal * TAX_RATE : 0;
    const total = subtotal + tax;
    return { subtotal, tax, total };
  }, [watchItems, parentDoc]);

  // Validación SUNAT: La NC no puede ser mayor al documento original
  useEffect(() => {
    if (totals.total > parentDoc?.totals?.total) {
      // Aquí podrías mostrar un error visual. Por ahora lo bloqueamos en el submit.
    }
  }, [totals, parentDoc]);

  if (!parentDoc) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">Documento original no encontrado.</Alert>
        <Button
          sx={{ mt: 2 }}
          startIcon={<ArrowBack />}
          onClick={() => history.push("/invoices")}
        >
          Volver
        </Button>
      </Container>
    );
  }

  // Filtrar motivos SUNAT según tipo de documento (El código 02 es solo para facturas)
  const filteredReasons = sunatReasons.filter((reason) => {
    if (parentDoc.docType === "BOLETA" && reason.code === "02") return false;
    return true;
  });

  const onSubmit = async (data) => {
    if (totals.total > parentDoc.totals.total) {
      setSnackbar({
        open: true,
        message:
          "Error: El monto de la NC no puede superar al documento original",
        severity: "error",
      });
      return;
    }

    const newCreditNote = {
      id: `NC01-${Math.floor(Math.random() * 9999)
        .toString()
        .padStart(4, "0")}`,
      docType: "NOTA_DE_CREDITO",
      parentDocType: parentDoc.docType,
      parentId: parentDoc.id,
      parentDate: parentDoc.date,
      reasonCode: data.reasonCode,
      client: parentDoc.client,
      items: data.items,
      totals: totals,
      date: new Date().toISOString(),
      status: "P",
    };
    
    console.log(newCreditNote);
    // Aquí iría la llamada al backend. Nosotros la guardamos en el estado local.
    // addDocument(newCreditNote);

    // Generar PDF
    // const doc = <CreditNotePDF data={newCreditNote} />;
    // const blob = await pdf(doc).toBlob();
    // const url = URL.createObjectURL(blob);
    // const link = document.createElement('a');
    // link.href = url;
    // link.download = `NOTA_CREDITO_${newCreditNote.id}.pdf`;
    // link.click();
    // URL.revokeObjectURL(url);

    setSnackbar({
      open: true,
      message: "Nota de Crédito generada exitosamente",
      severity: "success",
    });
    setTimeout(() => history.push("/"), 1500);
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 3,
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h4" fontWeight="bold" color="error">
            Generar Nota de Crédito
          </Typography>
          <Button
            startIcon={<ArrowBack />}
            variant="outlined"
            onClick={() => history.push("/invoices")}
          >
            Volver
          </Button>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Documento que se modifica: <strong>{parentDoc.documentTypeLabel}: {parentDoc.serie} - {parentDoc.correlative}</strong> 
        </Alert>
          <Box>
          Cliente: <strong>{parentDoc.userName}</strong>
          DNI / RUC: <strong>{parentDoc.userDni}</strong>
          {/* Monto Original: <strong>${parentDoc.totals.total.toFixed(2)}</strong> */}
          </Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="reasonCode"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label="Motivo SUNAT (Catálogo 09)"
                    error={!!errors.reasonCode}
                    helperText={errors.reasonCode?.message}
                  >
                    {filteredReasons.map((reason) => (
                      <MenuItem key={reason.code} value={reason.code}>
                        {reason.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            {/* El motivo '01' usualmente anula todo, podríamos poner lógica para auto-rellenar a 0 aquí */}
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Ítems a modificar
          </Typography>
          <Typography
            variant="caption"
            color="textSecondary"
            display="block"
            mb={2}
          >
            Ajuste la cantidad o el precio según el motivo seleccionado. El
            total resultante no debe superar el monto original.
          </Typography>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell>Descripción</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                  <TableCell align="right">Nuevo Precio</TableCell>
                  <TableCell align="right">Nuevo Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.map((field, index) => (
                  <TableRow key={field.id}>
                    <TableCell>
                      <Controller
                        name={`items.${index}.description`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            variant="standard"
                            size="small"
                            fullWidth
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Controller
                        name={`items.${index}.quantity`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            variant="standard"
                            type="number"
                            size="small"
                            inputProps={{
                              min: 0,
                              style: { textAlign: "right" },
                            }}
                            onChange={(e) =>
                              field.onChange(parseInt(e.target.value) || 0)
                            }
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <Controller
                        name={`items.${index}.amount`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            variant="standard"
                            type="number"
                            size="small"
                            inputProps={{
                              min: 0,
                              step: "0.01",
                              style: { textAlign: "right" },
                            }}
                            onChange={(e) =>
                              field.onChange(parseFloat(e.target.value) || 0)
                            }
                          />
                        )}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight="bold">
                        S/ 
                        {(
                          (Number(watchItems?.[index]?.quantity) || 0) *
                          (Number(watchItems?.[index]?.amount) || 0)
                        ).toFixed(2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Box sx={{ width: 300 }}>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography>Subtotal NC:</Typography>
                <Typography>S/ {totals.subtotal.toFixed(2)}</Typography>
              </Box>
              {parentDoc.docType === "FACTURA" && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography>IGV NC:</Typography>
                  <Typography>S/ {totals.tax.toFixed(2)}</Typography>
                </Box>
              )}
              <Divider />
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}
              >
                <Typography variant="h6" fontWeight="bold" color="error">
                  TOTAL NC:
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="error">
                  S/ {totals.total.toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}
          >
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => history.push("/")}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="error"
              size="large"
              startIcon={<Download />}
            >
              Generar Nota de Crédito
            </Button>
          </Box>
        </form>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
